import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { Trash2, Calendar, Video, AlertCircle, PlayCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminEventCleanup = () => {
  const [logs, setLogs] = useState([]);
  const [cleanupInfo, setCleanupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchCleanupInfo();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/event-cleanup/deletion-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs);
    } catch (error) {
      toast.error('Failed to load deletion logs');
    }
  };

  const fetchCleanupInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/event-cleanup/cleanup-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanupInfo(response.data);
    } catch (error) {
      console.error('Failed to load cleanup info');
    }
  };

  const handleRunCleanup = async () => {
    if (!window.confirm('Run cleanup now? This will delete events older than 14 days.')) {
      return;
    }

    setRunningCleanup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/event-cleanup/run-cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Cleanup completed! Deleted ${response.data.total_deleted} event(s)`);
      await fetchLogs();
      await fetchCleanupInfo();
    } catch (error) {
      toast.error('Failed to run cleanup');
    } finally {
      setRunningCleanup(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this log entry?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/event-cleanup/deletion-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log deleted');
      await fetchLogs();
    } catch (error) {
      toast.error('Failed to delete log');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllLogs = async () => {
    if (!window.confirm('Delete ALL logs? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/event-cleanup/deletion-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${response.data.count} log(s)`);
      await fetchLogs();
    } catch (error) {
      toast.error('Failed to delete logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Event Auto-Cleanup</h2>
        <div className="flex gap-2">
          <Button 
            onClick={() => { fetchLogs(); fetchCleanupInfo(); }}
            variant="outline"
            size="sm"
            className="bg-white text-gray-900 hover:bg-gray-100 border-gray-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleRunCleanup}
            disabled={runningCleanup}
            size="sm"
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            {runningCleanup ? 'Running...' : 'Run Cleanup Now'}
          </Button>
        </div>
      </div>

      {/* Cleanup Info Card */}
      {cleanupInfo && (
        <Card className="bg-gradient-to-r from-purple-50 to-cyan-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-purple-600" />
              Automatic Cleanup Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Cutoff Date</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(cleanupInfo.cutoff_date).split(',')[0]}
                </p>
                <p className="text-xs text-gray-500">Events on or before this date will be deleted</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Events Pending Deletion</p>
                <p className="text-lg font-semibold text-purple-600">
                  {cleanupInfo.events_to_delete.total}
                </p>
                <p className="text-xs text-gray-500">
                  {cleanupInfo.events_to_delete.calendar_events} calendar, {cleanupInfo.events_to_delete.livestreams} streams
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Cleanup</p>
                <p className="text-lg font-semibold text-gray-900">
                  {cleanupInfo.last_cleanup ? formatDate(cleanupInfo.last_cleanup).split(',')[0] : 'Never'}
                </p>
                {cleanupInfo.last_cleanup_deleted && (
                  <p className="text-xs text-gray-500">
                    Deleted {cleanupInfo.last_cleanup_deleted.calendar_events + cleanupInfo.last_cleanup_deleted.livestreams} event(s)
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>⏰ Schedule:</strong> Automatic cleanup runs daily at 2:00 AM server time. 
                Events are deleted 14 days after their event date.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Deletion History</CardTitle>
            {logs.length > 0 && (
              <Button 
                onClick={handleDeleteAllLogs}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Logs
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No deletion logs yet</p>
              <p className="text-sm mt-2">Logs will appear here when events are automatically deleted</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <Card key={log.id} className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">
                            {formatDate(log.cleanup_date)}
                          </Badge>
                          {log.triggered_by === 'manual' && (
                            <Badge variant="secondary">Manual</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Cutoff: {formatDate(log.cutoff_date).split(',')[0]}
                        </p>
                        {log.admin_email && (
                          <p className="text-xs text-gray-500">By: {log.admin_email}</p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleDeleteLog(log.id)}
                        variant="ghost"
                        size="sm"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-cyan-500" />
                        <div>
                          <p className="font-semibold text-gray-900">{log.calendar_events_deleted}</p>
                          <p className="text-sm text-gray-600">Calendar Events</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Video className="w-5 h-5 text-purple-500" />
                        <div>
                          <p className="font-semibold text-gray-900">{log.livestreams_deleted}</p>
                          <p className="text-sm text-gray-600">Live Streams</p>
                        </div>
                      </div>
                    </div>

                    {(log.calendar_events?.length > 0 || log.livestreams?.length > 0) && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-purple-600 hover:text-purple-700 font-medium">
                          View deleted items
                        </summary>
                        <div className="mt-3 space-y-3">
                          {log.calendar_events?.length > 0 && (
                            <div>
                              <p className="font-medium text-gray-700 mb-2">Calendar Events:</p>
                              <ul className="list-disc list-inside space-y-1 text-gray-600">
                                {log.calendar_events.map((event) => (
                                  <li key={event.id}>
                                    {event.title} ({event.event_date})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {log.livestreams?.length > 0 && (
                            <div>
                              <p className="font-medium text-gray-700 mb-2">Live Streams:</p>
                              <ul className="list-disc list-inside space-y-1 text-gray-600">
                                {log.livestreams.map((stream) => (
                                  <li key={stream.id}>
                                    {stream.title} ({stream.scheduled_date || 'Recorded'})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEventCleanup;
